import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { DineType, Product } from "@ai-pos/shared";
import { categoriesApi, productsApi } from "../api/resources.js";
import type { Category } from "../api/resources.js";

type PriceInputMode = "exclusive" | "inclusive";

interface FormState {
  id: string | null;
  name: string;
  nameKana: string;
  categoryId: string;
  priceInputMode: PriceInputMode;
  /** priceInputModeで指定した方の金額（税抜 or 税込）。保存時にpriceExcludingTaxへ変換する。 */
  priceInput: number;
  /** trueの場合、税抜/税込の切り替えボタンを押しても表示中の金額を変換せずそのまま保つ。 */
  lockPriceOnModeSwitch: boolean;
  taxRatePercent: number;
  dineType: DineType;
  sortOrder: number;
  isActive: boolean;
  isButtonVisible: boolean;
  isVariablePrice: boolean;
  aliasesText: string;
}

function emptyForm(categoryId: string): FormState {
  return {
    id: null,
    name: "",
    nameKana: "",
    categoryId,
    priceInputMode: "exclusive",
    priceInput: 0,
    lockPriceOnModeSwitch: false,
    taxRatePercent: 10,
    dineType: "BOTH",
    sortOrder: 0,
    isActive: true,
    isButtonVisible: true,
    isVariablePrice: false,
    aliasesText: "",
  };
}

/**
 * 税抜金額に税率を加算した税込金額を算出する（backendのaddTaxToExclusiveAmountと同じ方針）。
 * 1の位が出ないよう、10円未満は切り捨てる。
 */
function toInclusive(exclusiveAmount: number, taxRatePercent: number): number {
  const rawInclusive = exclusiveAmount + (exclusiveAmount * taxRatePercent) / 100;
  return Math.max(exclusiveAmount, Math.floor(rawInclusive / 10) * 10);
}

/**
 * 税込目標金額に対応する税抜金額を求める。
 * 税込価格は10円未満を切り捨てて算出するため、10円単位でない税込目標や、整数演算では
 * 「税抜→税込」の変換で到達できない税込金額が生じうる。単純な逆算式だけだと税込目標を
 * 超えてしまうことがあるため、周辺を探索し、税込目標ぴったりになる税抜金額があればそれを、
 * なければ税込目標を超えない範囲で最も近い税抜金額を採用する（お客様への過請求を避ける）。
 */
function toExclusiveForInclusiveTarget(inclusiveTarget: number, taxRatePercent: number): number {
  const guess = inclusiveTarget - Math.floor((inclusiveTarget * taxRatePercent) / (100 + taxRatePercent));
  let best = guess;
  let bestInclusive = -Infinity;
  for (let delta = -15; delta <= 15; delta++) {
    const candidate = guess + delta;
    if (candidate < 0) continue;
    const inclusive = toInclusive(candidate, taxRatePercent);
    if (inclusive === inclusiveTarget) return candidate;
    if (inclusive <= inclusiveTarget && inclusive > bestInclusive) {
      best = candidate;
      bestInclusive = inclusive;
    }
  }
  return best;
}

/** 現在のform入力（モードに関わらず）から、保存に使う税抜金額を算出する。 */
function resolveExcludingTax(form: FormState): number {
  return form.priceInputMode === "exclusive"
    ? form.priceInput
    : toExclusiveForInclusiveTarget(form.priceInput, form.taxRatePercent);
}

export function ProductAdmin() {
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [form, setForm] = useState<FormState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    Promise.all([productsApi.list(true), categoriesApi.list()])
      .then(([productsRes, categoriesRes]) => {
        setProducts(productsRes.products);
        setCategories(categoriesRes.categories);
      })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : "取得に失敗しました"));
  };

  useEffect(load, []);

  const openCreate = () => setForm(emptyForm(categories[0]?.id ?? ""));
  const openEdit = (product: Product) =>
    setForm({
      id: product.id,
      name: product.name,
      nameKana: product.nameKana,
      categoryId: product.categoryId,
      priceInputMode: "exclusive",
      priceInput: product.priceExcludingTax,
      lockPriceOnModeSwitch: false,
      taxRatePercent: product.taxRatePercent,
      dineType: product.dineType,
      sortOrder: product.sortOrder,
      isActive: product.isActive,
      isButtonVisible: product.isButtonVisible,
      isVariablePrice: product.isVariablePrice,
      aliasesText: product.aliases.join(", "),
    });

  const handleDelete = async (product: Product) => {
    if (!window.confirm(`「${product.name}」を削除します。よろしいですか？`)) return;
    setError(null);
    try {
      await productsApi.remove(product.id);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "削除に失敗しました");
    }
  };

  const handleSave = async () => {
    if (!form) return;
    setSaving(true);
    setError(null);
    const payload = {
      name: form.name,
      nameKana: form.nameKana,
      categoryId: form.categoryId,
      priceExcludingTax: resolveExcludingTax(form),
      taxRatePercent: form.taxRatePercent,
      dineType: form.dineType,
      sortOrder: form.sortOrder,
      isActive: form.isActive,
      isButtonVisible: form.isButtonVisible,
      isVariablePrice: form.isVariablePrice,
      aliases: form.aliasesText
        .split(",")
        .map((a) => a.trim())
        .filter((a) => a.length > 0),
    };
    try {
      if (form.id) {
        await productsApi.update(form.id, payload);
      } else {
        await productsApi.create(payload);
      }
      setForm(null);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "保存に失敗しました");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="stack">
      <div className="row">
        <h2 style={{ flex: 1 }}>商品管理</h2>
        <button className="btn" onClick={() => navigate("/admin/categories")}>
          カテゴリー管理
        </button>
        <button className="btn btn-primary" onClick={openCreate}>
          新規商品
        </button>
      </div>
      {error && <div className="alert alert-error">{error}</div>}
      <div className="card">
        <table>
          <thead>
            <tr>
              <th>商品名</th>
              <th>カテゴリー</th>
              <th>価格（税抜）</th>
              <th>税率</th>
              <th>状態</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {products.map((product) => (
              <tr key={product.id}>
                <td>{product.name}</td>
                <td>{product.categoryName}</td>
                <td>{product.isVariablePrice ? "都度入力" : `¥${product.priceExcludingTax.toLocaleString("ja-JP")}`}</td>
                <td>{product.taxRatePercent}%</td>
                <td>
                  {product.isActive ? <span className="badge">販売中</span> : <span className="badge badge-danger">停止中</span>}
                </td>
                <td>
                  <button className="btn" onClick={() => openEdit(product)}>
                    編集
                  </button>
                  <button className="btn btn-danger btn-outline" onClick={() => void handleDelete(product)}>
                    削除
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {form && (
        <div className="modal-backdrop" onClick={() => setForm(null)}>
          <div className="modal stack" onClick={(e) => e.stopPropagation()}>
            <h3>{form.id ? "商品編集" : "新規商品"}</h3>
            <div className="form-field">
              <label htmlFor="p-name">商品名</label>
              <input id="p-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="form-field">
              <label htmlFor="p-kana">商品名の読み</label>
              <input id="p-kana" value={form.nameKana} onChange={(e) => setForm({ ...form, nameKana: e.target.value })} />
            </div>
            <div className="form-field">
              <label htmlFor="p-category">カテゴリー</label>
              <select id="p-category" value={form.categoryId} onChange={(e) => setForm({ ...form, categoryId: e.target.value })}>
                {categories
                  .filter((c) => !c.parentCategoryId)
                  .sort((a, b) => a.sortOrder - b.sortOrder)
                  .flatMap((parent) => [
                    parent,
                    ...categories.filter((c) => c.parentCategoryId === parent.id).sort((a, b) => a.sortOrder - b.sortOrder),
                  ])
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.parentCategoryId ? `    └ ${c.name}` : c.name}
                    </option>
                  ))}
              </select>
            </div>
            <div className="row">
              <label className="row" style={{ gap: 6 }}>
                <input
                  type="checkbox"
                  checked={form.isVariablePrice}
                  onChange={(e) => setForm({ ...form, isVariablePrice: e.target.checked })}
                  style={{ minHeight: "auto", width: 20, height: 20 }}
                />
                金額を都度入力する（チップなど固定価格がない商品）
              </label>
            </div>
            <div className="form-field">
              <label>入力する価格の種類</label>
              <div className="chip-row">
                <button
                  type="button"
                  className={form.priceInputMode === "exclusive" ? "chip active" : "chip"}
                  onClick={() => {
                    if (form.priceInputMode === "exclusive") return;
                    setForm({
                      ...form,
                      priceInputMode: "exclusive",
                      priceInput: form.lockPriceOnModeSwitch
                        ? form.priceInput
                        : toExclusiveForInclusiveTarget(form.priceInput, form.taxRatePercent),
                    });
                  }}
                >
                  税抜で入力
                </button>
                <button
                  type="button"
                  className={form.priceInputMode === "inclusive" ? "chip active" : "chip"}
                  onClick={() => {
                    if (form.priceInputMode === "inclusive") return;
                    setForm({
                      ...form,
                      priceInputMode: "inclusive",
                      priceInput: form.lockPriceOnModeSwitch
                        ? form.priceInput
                        : toInclusive(form.priceInput, form.taxRatePercent),
                    });
                  }}
                >
                  税込で入力
                </button>
              </div>
              <label className="row" style={{ gap: 6, marginTop: 6 }}>
                <input
                  type="checkbox"
                  checked={form.lockPriceOnModeSwitch}
                  onChange={(e) => setForm({ ...form, lockPriceOnModeSwitch: e.target.checked })}
                  style={{ minHeight: "auto", width: 20, height: 20 }}
                />
                金額を固定する（切り替えボタンを押しても金額を変換しない）
              </label>
            </div>
            <div className="row">
              <div className="form-field" style={{ flex: 1 }}>
                <label htmlFor="p-price">
                  {form.priceInputMode === "exclusive" ? "税抜価格（円）" : "税込価格（円）"}
                  {form.isVariablePrice ? "・レジ画面での初期値" : ""}
                </label>
                <input
                  id="p-price"
                  type="number"
                  value={form.priceInput}
                  onChange={(e) => setForm({ ...form, priceInput: Number(e.target.value) })}
                />
              </div>
              <div className="form-field" style={{ flex: 1 }}>
                <label htmlFor="p-tax">税率（%）</label>
                <input
                  id="p-tax"
                  type="number"
                  value={form.taxRatePercent}
                  onChange={(e) => setForm({ ...form, taxRatePercent: Number(e.target.value) })}
                />
              </div>
            </div>
            <p style={{ fontSize: 12.5, color: "var(--color-text-muted)", margin: "-6px 0 0" }}>
              税抜：¥{resolveExcludingTax(form).toLocaleString("ja-JP")} ／ 税込：¥
              {toInclusive(resolveExcludingTax(form), form.taxRatePercent).toLocaleString("ja-JP")}
              （レジ・伝票には税込価格が表示されます）
            </p>
            <div className="form-field">
              <label htmlFor="p-dine">店内・持ち帰り区分</label>
              <select
                id="p-dine"
                value={form.dineType}
                onChange={(e) => setForm({ ...form, dineType: e.target.value as DineType })}
              >
                <option value="BOTH">両方</option>
                <option value="DINE_IN">店内</option>
                <option value="TAKEOUT">持ち帰り</option>
              </select>
            </div>
            <div className="form-field">
              <label htmlFor="p-aliases">AI解析用の別名（カンマ区切り）</label>
              <input
                id="p-aliases"
                value={form.aliasesText}
                onChange={(e) => setForm({ ...form, aliasesText: e.target.value })}
              />
            </div>
            <div className="row">
              <label className="row" style={{ gap: 6 }}>
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                  style={{ minHeight: "auto", width: 20, height: 20 }}
                />
                販売中
              </label>
              <label className="row" style={{ gap: 6 }}>
                <input
                  type="checkbox"
                  checked={form.isButtonVisible}
                  onChange={(e) => setForm({ ...form, isButtonVisible: e.target.checked })}
                  style={{ minHeight: "auto", width: 20, height: 20 }}
                />
                レジ画面に表示
              </label>
            </div>
            <div className="row">
              <button className="btn" onClick={() => setForm(null)}>
                キャンセル
              </button>
              <button className="btn btn-primary" onClick={() => void handleSave()} disabled={saving || !form.name || !form.categoryId}>
                保存
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
